import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ShipmentList from "./components/ShipmentList";
import ShipmentChatPanel from "./components/ShipmentChatPanel";
import ShipmentDetailDrawer from "./components/ShipmentDetailDrawer";
import MobileShipmentsView from "./components/MobileShipmentsView";
import { useInfiniteShipments } from "./hooks/useInfiniteShipments";
import { ShipmentListLoadingSkeleton } from "@/features/shipments/ShipmentListItemSkeleton";
import PageTitle from "@/components/layout/PageTitle";
import ShipmentsEmptyState from "@/components/shipments/ShipmentsEmptyState";
import StartShipmentButton from "@/components/shipments/StartShipmentButton";
import { goToNewShipmentChat } from "@/utils/startNewShipment";

export default function ShipmentsPage({ onToggleSidebar }: { onToggleSidebar?: () => void }) {
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedId, setSelectedId] = useState<string | null>(urlId ?? null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<"details" | "documents">("details");
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  const { shipments, isLoading, isError, hasMore, loadMore, isFetchingMore } = useInfiniteShipments();

  const selectedShipment = useMemo(
    () => shipments.find((s: any) => s.id === selectedId) ?? null,
    [shipments, selectedId],
  );

  useEffect(() => {
    if (urlId) setSelectedId(urlId);
  }, [urlId]);

  useEffect(() => {
    const state = location.state as {
      openDetails?: boolean;
      drawerTab?: "details" | "documents";
    } | null;
    if (!state?.openDetails || !urlId) return;

    setDetailsOpen(true);
    if (state.drawerTab) setDrawerTab(state.drawerTab);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, urlId]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setDetailsOpen(false);
    navigate(`/shipments/${id}`, { replace: true });
  };

  const handleStartShipment = () => goToNewShipmentChat(navigate);

  const startShipmentAction = <StartShipmentButton onClick={handleStartShipment} />;

  if (isMobile) {
    return (
      <MobileShipmentsView
        shipments={shipments}
        isLoading={isLoading}
        onMenuClick={onToggleSidebar}
        hasMore={hasMore}
        isLoadingMore={isFetchingMore}
        onLoadMore={loadMore}
      />
    );
  }

  if (isError) {
    return (
      <div className="flex h-screen overflow-hidden items-center justify-center">
        <p className="text-red-500 text-[14px]">Failed to load shipments.</p>
      </div>
    );
  }

  if (!isLoading && shipments.length === 0) {
    return (
      <div className="flex h-screen flex-col overflow-hidden bg-white">
        <PageTitle actions={startShipmentAction}>Shipments</PageTitle>
        <ShipmentsEmptyState className="flex-1" onStartShipment={handleStartShipment} />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="w-[420px] min-w-[280px] border-r border-[#D0D5DD] flex flex-col bg-[#FAFAFA]">
        <PageTitle actions={startShipmentAction}>Shipments</PageTitle>

        {isLoading && shipments.length === 0 ? (
          <div className="flex-1 overflow-y-auto px-3 py-3">
            <ShipmentListLoadingSkeleton rows={4} />
          </div>
        ) : (
          <ShipmentList
            shipments={shipments}
            selectedId={selectedId}
            onSelect={handleSelect}
            hasMore={hasMore}
            isLoadingMore={isFetchingMore}
            onLoadMore={loadMore}
          />
        )}
      </div>

      <div className="flex-1 flex flex-col min-w-0 border-r border-[#E4E7EC]">
        {selectedShipment ? (
          <ShipmentChatPanel shipment={selectedShipment} onOpenDetails={() => setDetailsOpen(true)} />
        ) : (
          <ShipmentsEmptyState variant="selection" className="h-full" />
        )}
      </div>

      <ShipmentDetailDrawer
        isOpen={detailsOpen && !!selectedShipment}
        shipment={selectedShipment}
        onClose={() => setDetailsOpen(false)}
        initialMainTab={drawerTab}
      />
    </div>
  );
}
